/**
 * Utility functions for printing labels
 */

/**
 * Print-friendly CSS styles
 * ทำให้สวยงามเหมือน preview ในแอพ
 */
export const printStyles = `
  @media print {
    @page {
      margin: 0.5cm;
      size: A4;
    }
    
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    
    body {
      margin: 0;
      padding: 0;
      background: white !important;
      font-family: 'Sarabun', 'Kanit', Arial, sans-serif !important;
    }
    
    /* Hide navigation and UI elements */
    nav, header, footer, button, .no-print {
      display: none !important;
    }
    
    /* Show only print content */
    .print-content {
      display: block !important;
      background: white !important;
      color: #000 !important;
    }
    
    /* Preserve all styles */
    .print-content * {
      color: inherit !important;
      background-color: inherit !important;
      border-color: inherit !important;
    }
    
    /* Preserve borders */
    .print-content .border,
    .print-content [class*="border"] {
      border-style: solid !important;
      border-width: inherit !important;
    }
    
    /* Preserve background colors */
    .print-content .bg-gray-50,
    .print-content .bg-gray-100,
    .print-content .bg-yellow-50,
    .print-content .bg-blue-50 {
      background-color: inherit !important;
    }
    
    /* Preserve rounded corners */
    .print-content .rounded,
    .print-content .rounded-lg {
      border-radius: inherit !important;
    }
    
    /* Prevent page breaks inside important elements */
    .print-section {
      page-break-inside: avoid;
    }
    
    /* Ensure images print correctly */
    .print-content img {
      max-width: 100% !important;
      height: auto !important;
      display: block !important;
    }
    
    /* Preserve font weights */
    .print-content .font-bold,
    .print-content .font-semibold {
      font-weight: inherit !important;
    }
  }
`;

/**
 * Template sizes
 */
export const LABEL_TEMPLATES = {
  STICKER: {
    name: 'Sticker (10x15cm)',
    width: '10cm',
    height: '15cm',
    className: 'sticker-template'
  },
  A4: {
    name: 'A4',
    width: '21cm',
    height: '29.7cm',
    className: 'a4-template'
  },
  COMPACT: {
    name: 'Compact (8x12cm)',
    width: '8cm',
    height: '12cm',
    className: 'compact-template'
  }
};

/**
 * Open print dialog
 */
export const openPrintDialog = (contentId) => {
  const printWindow = window.open('', '_blank');
  const content = document.getElementById(contentId);
  
  if (!content) {
    console.error('Print content not found');
    return;
  }
  
  // Clone content with all styles preserved
  const clonedContent = content.cloneNode(true);
  
  // Add inline styles to preserve appearance
  const addInlineStyles = (element) => {
    if (element.nodeType === 1) { // Element node
      const computedStyle = window.getComputedStyle(element);
      
      // Copy important styles
      const importantStyles = {
        color: computedStyle.color,
        backgroundColor: computedStyle.backgroundColor,
        borderColor: computedStyle.borderColor,
        borderWidth: computedStyle.borderWidth,
        borderStyle: computedStyle.borderStyle,
        borderRadius: computedStyle.borderRadius,
        padding: computedStyle.padding,
        margin: computedStyle.margin,
        fontSize: computedStyle.fontSize,
        fontWeight: computedStyle.fontWeight,
        fontFamily: computedStyle.fontFamily,
        lineHeight: computedStyle.lineHeight,
        textAlign: computedStyle.textAlign,
        display: computedStyle.display,
        flexDirection: computedStyle.flexDirection,
        justifyContent: computedStyle.justifyContent,
        alignItems: computedStyle.alignItems,
        gap: computedStyle.gap
      };
      
      // Apply inline styles
      let styleString = '';
      Object.entries(importantStyles).forEach(([key, value]) => {
        if (value && value !== 'rgba(0, 0, 0, 0)' && value !== 'transparent' && value !== 'none') {
          const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
          styleString += `${cssKey}: ${value} !important; `;
        }
      });
      
      if (styleString) {
        element.setAttribute('style', styleString + (element.getAttribute('style') || ''));
      }
      
      // Process children
      Array.from(element.children).forEach(child => addInlineStyles(child));
    }
  };
  
  addInlineStyles(clonedContent);
  
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Print Label</title>
        <meta charset="UTF-8">
        <style>
          ${printStyles}
          
          /* Base styles */
          * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
          }
          
          body {
            font-family: 'Sarabun', 'Kanit', Arial, sans-serif !important;
            margin: 0;
            padding: 20px;
            background: white !important;
            color: #000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          .print-content {
            width: 100%;
            max-width: 100%;
            background: white !important;
            color: #000 !important;
            margin: 0 auto;
          }
          
          /* Preserve Tailwind-like classes */
          .print-content .bg-gray-50 {
            background-color: #f9fafb !important;
          }
          
          .print-content .bg-gray-100 {
            background-color: #f3f4f6 !important;
          }
          
          .print-content .bg-yellow-50 {
            background-color: #fefce8 !important;
          }
          
          .print-content .bg-blue-50 {
            background-color: #eff6ff !important;
          }
          
          .print-content .border-gray-300 {
            border-color: #d1d5db !important;
          }
          
          .print-content .border-gray-200 {
            border-color: #e5e7eb !important;
          }
          
          .print-content .border-yellow-300 {
            border-color: #fde047 !important;
          }
          
          .print-content .border-gray-800 {
            border-color: #1f2937 !important;
          }
          
          .print-content .text-gray-800 {
            color: #1f2937 !important;
          }
          
          .print-content .text-gray-700 {
            color: #374151 !important;
          }
          
          .print-content .text-gray-600 {
            color: #4b5563 !important;
          }
          
          .print-content .text-gray-500 {
            color: #6b7280 !important;
          }
          
          .print-content .text-gray-900 {
            color: #111827 !important;
          }
          
          .print-content .font-bold {
            font-weight: 700 !important;
          }
          
          .print-content .font-semibold {
            font-weight: 600 !important;
          }
          
          .print-content .rounded {
            border-radius: 0.25rem !important;
          }
          
          .print-content .rounded-lg {
            border-radius: 0.5rem !important;
          }
          
          .print-content img {
            max-width: 100%;
            height: auto;
            display: block;
          }
        </style>
      </head>
      <body>
        ${clonedContent.innerHTML}
      </body>
    </html>
  `);
  
  printWindow.document.close();
  printWindow.focus();
  
  // Wait for content to load before printing
  setTimeout(() => {
    printWindow.print();
    // Close window after printing (optional)
    // printWindow.close();
  }, 250);
};

/**
 * Download as image (for sharing to LINE)
 */
export const downloadAsImage = async (contentId, filename = 'label.png') => {
  try {
    // Use html2canvas if available, or fallback to screenshot
    const content = document.getElementById(contentId);
    if (!content) {
      throw new Error('Content not found');
    }
    
    // For now, use a simple approach
    // In production, you might want to use html2canvas library
    alert('ฟีเจอร์ดาวน์โหลดเป็นรูปภาพจะพร้อมใช้งานเร็วๆ นี้');
  } catch (error) {
    console.error('Error downloading image:', error);
    alert('เกิดข้อผิดพลาดในการดาวน์โหลดรูปภาพ');
  }
};

/**
 * Share to LINE (Deprecated - ใช้ใน StoreLabelPrintModal แทน)
 */
export const shareToLine = (text, url) => {
  const lineUrl = `https://line.me/R/msg/text/?${encodeURIComponent(text + ' ' + url)}`;
  window.open(lineUrl, '_blank');
};

